import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactFlow, {
    addEdge,
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    Panel,
    useReactFlow,
    ReactFlowProvider,
    MarkerType
} from 'reactflow';
import { TYPE_COLORS } from './nodes/nodeStyles';
import 'reactflow/dist/style.css';
import { Layout, Button, Avatar, Space, Typography, Tooltip, Drawer, Popover, Tabs, Input, Spin, message, Dropdown, Menu, Select, Popconfirm, ColorPicker, Modal } from 'antd';
import {
    ArrowLeftOutlined,
    CloudSyncOutlined,
    SettingOutlined,
    ShareAltOutlined,
    PictureOutlined,
    FontSizeOutlined,
    VideoCameraOutlined,
    PlusOutlined,
    DragOutlined,
    LeftCircleOutlined,
    AppstoreOutlined,
    UserOutlined,
    ScissorOutlined,
    FileTextOutlined,
    FireOutlined,
    ApiOutlined,
    SearchOutlined,
    ScanOutlined,
    UploadOutlined,
    BarsOutlined,
    LoadingOutlined,
    GatewayOutlined,
    GroupOutlined,
    CloseOutlined,
    SoundOutlined,
    DeploymentUnitOutlined,
    BgColorsOutlined,
    CodepenOutlined,
    MobileOutlined,
    TabletOutlined,
    DesktopOutlined,
    BorderOutlined,
    EyeOutlined,
    UndoOutlined,
    RedoOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Logo } from '../../images/pxlplygrnd-logo.svg';

import MediaNode from './nodes/MediaNode';
import TextNode from './nodes/TextNode';
import VideoNode from './nodes/VideoNode';
import CodeNode from './nodes/CodeNode';
import QwenNode from './nodes/QwenNode';
import MidjourneyNode from './nodes/MidjourneyNode';
import SegmentationNode from './nodes/SegmentationNode'; // Import New Node
import TextToSpeechNode from './nodes/TextToSpeechNode'; // Import New Node
import OctalysisNode from './nodes/OctalysisNode'; // Import New Node
import ColorAnalysisNode from './nodes/ColorAnalysisNode'; // Import New Node
import ColorPaletteNode from './nodes/ColorPaletteNode'; // Import New Node
import Hunyuan3DNode from './nodes/Hunyuan3DNode'; // Import New Node
import TopazUpscaleNode from './nodes/TopazUpscaleNode'; // Import New Node
import HunyuanPanoramaNode from './nodes/HunyuanPanoramaNode';
import FontExtractNode from './nodes/FontExtractNode'; // Import New Node
import VeoNode from './nodes/VeoNode'; // Import New Node
import MarigoldNode from './nodes/MarigoldNode'; // Import New Node
import ImageSlicingNode from './nodes/ImageSlicingNode';
import TextSlicingNode from './nodes/TextSlicingNode';
import MoondreamQueryNode from './nodes/MoondreamQueryNode';
import DynamicTextNode from './nodes/DynamicTextNode';
import api from '../../api/client';
import { useQueryClient } from '@tanstack/react-query';
import { useInfiniteMediaGenerations, useInfiniteStorageFiles } from '../../api/canvasApi';

import { useCanvas, useCanvases, useCanvasNodes, useCanvasEdges, useCreateNode, useUpdateNode, useDeleteNode, useCreateEdge, useDeleteEdge, useUpdateCanvas, useCanvasNodeGroups, useCreateNodeGroup, useUpdateNodeGroup, useDeleteNodeGroup, useCreateCanvas, useDeleteGeneratedMedia } from '../../api/canvasApi';
import { findNonOverlappingPosition } from './nodePlacement';
import MoodboardNode from './nodes/MoodboardNode';
import GroupNode from './nodes/GroupNode';
import CanvasMediaGallery from './components/CanvasMediaGallery';
import VideoEditor from './components/VideoEditor';
import PromptLibraryDrawer from './components/PromptLibraryDrawer';
import MediaPreviewModal from './components/MediaPreviewModal';
import MidjourneyPanel from './components/MidjourneyPanel';
import PinterestPanel from './components/PinterestPanel';
import GeminiChatPanel from './components/GeminiChatPanel';
import SrefStylesTab from './components/SrefStylesTab';
import RightPanelStack from './components/RightPanelStack';
import UIDesignEditor from './UIDesignEditor';
import { UIDesignProvider, useUIDesign } from './contexts/UIDesignContext';
import ColorPalettesPopover from './components/ColorPalettesPopover';
import ScrapbookPanel from './components/ScrapbookPanel';
import GlyphEditorPanel from './components/GlyphEditorPanel';
import './canvasPerformance.css';

const { Header, Content } = Layout;
const { Text } = Typography;

const nodeTypes = {
    media: MediaNode,
    text: TextNode,
    video: VideoNode,
    code: CodeNode,
    qwen: QwenNode,
    imageSlicer: ImageSlicingNode,
    textSlicer: TextSlicingNode,
    moodboard: MoodboardNode,
    group: GroupNode,
    textToSpeech: TextToSpeechNode,
    midjourney: MidjourneyNode,
    segmentation: SegmentationNode,
    octalysis: OctalysisNode,
    colorAnalysis: ColorAnalysisNode,
    octalysis: OctalysisNode,
    colorAnalysis: ColorAnalysisNode,
    colorPaletteDisplay: ColorPaletteNode,
    hunyuan3d: Hunyuan3DNode,
    topaz: TopazUpscaleNode,
    hunyuanPanorama: HunyuanPanoramaNode,
    fontExtract: FontExtractNode,
    veo: VeoNode,
    marigoldDepth: MarigoldNode,
    moondreamQuery: MoondreamQueryNode,
    dynamicText: DynamicTextNode
};

const Editor = () => {
    const { projectId: canvasId } = useParams();
    const navigate = useNavigate();
    const reactFlowWrapper = useRef(null);
    const { screenToFlowPosition, getNodes } = useReactFlow();
    const queryClient = useQueryClient();

    // API Queries
    const { data: canvas, isLoading: canvasLoading } = useCanvas(canvasId);
    const { data: dbNodesData, isLoading: nodesLoading } = useCanvasNodes(canvasId);
    const { data: dbEdgesData, isLoading: edgesLoading } = useCanvasEdges(canvasId);
    const { data: nodeGroupsData } = useCanvasNodeGroups(canvasId);
    const { data: allCanvases = [] } = useCanvases();

    // UI Design toolbar state (shared with UIDesignEditor)
    const { viewMode: uiViewMode, setViewMode: setUiViewMode, showGrid: uiShowGrid, setShowGrid: setUiShowGrid } = useUIDesign();

    const dbNodes = dbNodesData || [];
    const dbEdges = dbEdgesData || [];
    const nodeGroups = nodeGroupsData || [];

    // API Mutations
    const createNodeMutation = useCreateNode(canvasId);
    const updateNodeMutation = useUpdateNode(canvasId);
    const deleteNodeMutation = useDeleteNode(canvasId);
    const createEdgeMutation = useCreateEdge(canvasId);
    const deleteEdgeMutation = useDeleteEdge(canvasId);
    const updateCanvasMutation = useUpdateCanvas();
    const createNodeGroupMutation = useCreateNodeGroup(canvasId);
    const deleteNodeGroupMutation = useDeleteNodeGroup(canvasId);
    const createCanvasMutation = useCreateCanvas();
    const deleteGeneratedMediaMutation = useDeleteGeneratedMedia(canvasId);

    // React Flow State
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [isSaving, setIsSaving] = useState(false);

    // UI State
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [activeTool, setActiveTool] = useState('select');
    const [addNodeMenuOpen, setAddNodeMenuOpen] = useState(false);
    const [mediaViewMode, setMediaViewMode] = useState('grid');
    const [viewMode, setViewMode] = useState('canvas'); // 'canvas' | 'video'
    const [isEditingName, setIsEditingName] = useState(false);
    const [editingName, setEditingName] = useState('');
    const updateNodeGroupMutation = useUpdateNodeGroup(canvasId);
    const { mutateAsync: updateNode } = updateNodeMutation;
    const { mutateAsync: updateNodeGroup } = updateNodeGroupMutation;

    // Media State
    // Custom hook for per-sentinel infinite scroll with callback refs
    const useInfiniteScroll = (fetchNext, hasMore, isFetching) => {
        const [node, setNode] = useState(null);
        const sentinelRef = useCallback((el) => setNode(el), []);

        useEffect(() => {
            if (!node || !hasMore || isFetching) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) fetchNext();
                },
                { threshold: 0, rootMargin: '0px 0px 400px 0px' }
            );

            observer.observe(node);
            return () => observer.disconnect();
        }, [node, fetchNext, hasMore, isFetching]);

        return sentinelRef;
    };
    const [promptLibraryVisible, setPromptLibraryVisible] = useState(false);
    const [colorPalettesVisible, setColorPalettesVisible] = useState(false);
    const [mediaDrawerWidth, setMediaDrawerWidth] = useState(340);
    const [mjCollapsed, setMjCollapsed] = useState(true);
    const [pinterestCollapsed, setPinterestCollapsed] = useState(true);
    const [geminiCollapsed, setGeminiCollapsed] = useState(true);

    // Scrapbook State
    const [scrapbookOpen, setScrapbookOpen] = useState(false);
    const [scrapbookImage, setScrapbookImage] = useState(null);
    const [glyphEditorOpen, setGlyphEditorOpen] = useState(false);

    // Media Filtration State
    const [mediaFilterType, setMediaFilterType] = useState('all');
    const [mediaFilterCanvas, setMediaFilterCanvas] = useState('all');
    const [drawerActiveTab, setDrawerActiveTab] = useState('1');
    const [deletingMediaId, setDeletingMediaId] = useState(null);

    // Inspiration Media State
    const [inspirationFolderFilter, setInspirationFolderFilter] = useState('all');

    // Infinite queries for media
    const {
        data: generationsData,
        fetchNextPage: fetchNextGenerations,
        hasNextPage: hasMoreGenerations,
        isFetchingNextPage: isFetchingMoreGenerations,
        isLoading: generationsLoading
    } = useInfiniteMediaGenerations(null, 24, mediaFilterType === 'all' ? null : mediaFilterType);

    const {
        data: uploadsData,
        fetchNextPage: fetchNextUploads,
        hasNextPage: hasMoreUploads,
        isFetchingNextPage: isFetchingMoreUploads,
        isLoading: uploadsLoading
    } = useInfiniteStorageFiles(drawerVisible ? 'media-uploads' : null, 24);

    const {
        data: inspirationQueryData,
        fetchNextPage: fetchNextInspiration,
        hasNextPage: hasMoreInspiration,
        isFetchingNextPage: isFetchingMoreInspiration,
        isLoading: inspirationLoading
    } = useInfiniteStorageFiles(drawerVisible ? 'inspiration-from-the-web' : null, 24);

    const mediaLoading = uploadsLoading || generationsLoading;
    const inspirationFolders = [
        { value: 'all', label: 'All Folders' },
        { value: 'branding', label: 'Branding' },
        { value: 'effects', label: 'Effects' },
        { value: 'midjourney', label: 'Midjourney' },
        { value: 'people', label: 'People' },
        { value: 'places', label: 'Places' },
        { value: 'things', label: 'Things' }
    ];

    const isResizingDrawer = useRef(false);

    // Media Preview State
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewStartIndex, setPreviewStartIndex] = useState(0);
    const [previewMediaList, setPreviewMediaList] = useState([]);

    // Slot Selection State (for multi-input nodes like VideoNode)
    const [slotSelectionVisible, setSlotSelectionVisible] = useState(false);
    const [pendingConnection, setPendingConnection] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);

    // Quick-Connect State (double-click source, click target to connect)
    const [quickConnectSourceId, setQuickConnectSourceId] = useState(null);

    // Configuration for nodes with multiple image input slots
    const MULTI_IMAGE_INPUT_NODES = {
        video: {
            slots: [
                { id: 'left-middle', label: 'Start Frame', description: 'Required - First frame of video', required: true },
                { id: 'left-bottom', label: 'End Frame', description: 'Optional - Last frame of video', required: false }
            ]
        },
        hunyuan3d: {
            slots: [
                { id: 'input-front', label: 'Front View', description: 'Required - Front view of object', required: true },
                { id: 'input-back', label: 'Back View', description: 'Optional - Back view', required: false },
                { id: 'input-left', label: 'Left View', description: 'Optional - Left side view', required: false },
                { id: 'input-right', label: 'Right View', description: 'Optional - Right side view', required: false }
            ]
        }
    };

    const handleMediaClick = (index, list) => {
        setPreviewStartIndex(index);
        setPreviewMediaList(list);
        setPreviewVisible(true);
    };

    const handleDeleteGeneratedMedia = async (e, mediaId) => {
        e.stopPropagation();
        setDeletingMediaId(mediaId);
        try {
            await deleteGeneratedMediaMutation.mutateAsync(mediaId);
            message.success('Media deleted');
            // react-query's useDeleteGeneratedMedia already invalidates the cache
        } catch (error) {
            console.error('Failed to delete media:', error);
            message.error('Failed to delete media');
        }
        setDeletingMediaId(null);
    };

    const handleDeleteInspirationMedia = async (e, item) => {
        e.stopPropagation();
        setDeletingMediaId(item.id);
        try {
            await api.delete('/storage/file', { data: { path: item.path } });
            message.success('Inspiration media deleted');
            // Invalidate the infinite query cache so it refetches
            queryClient.invalidateQueries({ queryKey: ['storage', 'infinite', 'inspiration-from-the-web'] });
        } catch (error) {
            console.error('Failed to delete inspiration media:', error);
            message.error('Failed to delete media');
        }
        setDeletingMediaId(null);
    };

    // Drawer Resizing Logic
    const handleDrawerMouseDown = (e) => {
        e.preventDefault();
        isResizingDrawer.current = true;
        document.addEventListener('mousemove', handleDrawerMouseMove);
        document.addEventListener('mouseup', handleDrawerMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none'; // Prevent selection while dragging
    };

    const handleDrawerMouseMove = useCallback((e) => {
        if (!isResizingDrawer.current) return;

        // Calculate new width: Window Width - Mouse X
        // Since the drawer is on the right, the width is the distance from the right edge
        const newWidth = window.innerWidth - e.clientX;

        // Clamp width between min and max
        if (newWidth >= 250 && newWidth <= 800) {
            setMediaDrawerWidth(newWidth);
        }
    }, []);

    const handleDrawerMouseUp = useCallback(() => {
        isResizingDrawer.current = false;
        document.removeEventListener('mousemove', handleDrawerMouseMove);
        document.removeEventListener('mouseup', handleDrawerMouseUp);
        document.body.style.cursor = 'default';
        document.body.style.userSelect = 'auto';
    }, [handleDrawerMouseMove]);

    // Cleanup event listeners on unmount
    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleDrawerMouseMove);
            document.removeEventListener('mouseup', handleDrawerMouseUp);
        };
    }, [handleDrawerMouseMove, handleDrawerMouseUp]);

    // Open Tabs State (persisted to localStorage)
    const [openTabs, setOpenTabs] = useState(() => {
        const saved = localStorage.getItem('pxl_open_tabs');
        return saved ? JSON.parse(saved) : [];
    });

    // Persist open tabs to localStorage
    useEffect(() => {
        localStorage.setItem('pxl_open_tabs', JSON.stringify(openTabs));
    }, [openTabs]);

    // Ensure current canvas is in open tabs
    useEffect(() => {
        if (canvasId) {
            setOpenTabs(prev => {
                if (prev.includes(canvasId)) {
                    return prev; // Don't add duplicate
                }
                return [...prev, canvasId];
            });
        }
    }, [canvasId]);

    // Tab management functions
    const closeTab = (tabId, e) => {
        e?.stopPropagation();
        const newTabs = openTabs.filter(id => id !== tabId);
        setOpenTabs(newTabs);

        // If closing the current tab, navigate to another open tab or dashboard
        if (tabId === canvasId) {
            if (newTabs.length > 0) {
                navigate(`/canvas/${newTabs[newTabs.length - 1]}`);
            } else {
                navigate('/dashboard');
            }
        }
    };

    const switchTab = (tabId) => {
        if (tabId !== canvasId) {
            navigate(`/canvas/${tabId}`);
        }
    };

    const addCanvasToTabs = (newCanvasId) => {
        if (!openTabs.includes(newCanvasId)) {
            setOpenTabs(prev => [...prev, newCanvasId]);
        }
        navigate(`/canvas/${newCanvasId}`);
    };

    // Get canvas names for tabs
    const getCanvasName = (id) => {
        if (id === canvasId) return canvas?.name || 'Untitled';
        const found = allCanvases.find(c => c.id === id);
        return found?.name || 'Untitled';
    };

    // Transform helpers for paginated data
    const toStorageItem = (file) => {
        const ext = file.name ? file.name.split('.').pop().toLowerCase() : '';
        let type = 'image';
        if (['mp4', 'webm', 'mov'].includes(ext)) type = 'video';
        if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) type = 'audio';
        if (['glb', 'gltf'].includes(ext)) type = 'model';
        return {
            id: file.path, type, src: file.url, label: file.name,
            canvasId: file.customMetadata?.canvasId || 'unknown',
            canvasName: file.customMetadata?.canvasName || 'Unknown',
            prompt: '', model: '', provider: '', metadata: {},
            createdAt: file.timeCreated
        };
    };

    const toGenerationItem = (gen) => ({
        id: gen.id, generationId: gen.generation_id,
        type: gen.type || 'image', src: gen.url,
        label: gen.prompt ? gen.prompt.slice(0, 30) : 'Generated Media',
        canvasId: gen.canvas_id || 'unknown', canvasName: 'Unknown',
        prompt: gen.prompt || '', model: gen.model || '',
        provider: gen.provider || '', metadata: gen.metadata || {},
        createdAt: gen.created_at
    });

    const toInspirationItem = (file) => {
        const ext = file.name ? file.name.split('.').pop().toLowerCase() : '';
        let type = 'image';
        if (['mp4', 'webm', 'mov'].includes(ext)) type = 'video';
        return {
            id: file.path, type, src: file.url, label: file.name,
            path: file.path, folder: file.folder || 'uncategorized',
            notes: file.customMetadata?.notes || '', metadata: file.customMetadata || {}
        };
    };

    // Flatten paginated data
    const importedMedia = useMemo(() => {
        if (!uploadsData?.pages) return [];
        return uploadsData.pages.flatMap(page => page.data.map(f => toStorageItem(f)));
    }, [uploadsData]);

    const generatedMedia = useMemo(() => {
        if (!generationsData?.pages) return [];
        return generationsData.pages.flatMap(page => page.data.map(f => toGenerationItem(f)));
    }, [generationsData]);

    const inspirationMedia = useMemo(() => {
        if (!inspirationQueryData?.pages) return [];
        return inspirationQueryData.pages.flatMap(page => page.data.map(f => toInspirationItem(f)));
    }, [inspirationQueryData]);

    // Derived data
    const availableCanvases = useMemo(() => {
        const canvases = new Set();
        [...importedMedia, ...generatedMedia].forEach(item => {
            if (item.canvasName && item.canvasName !== 'Unknown') canvases.add(item.canvasName);
        });
        return Array.from(canvases).sort();
    }, [importedMedia, generatedMedia]);

    const filteredImportedMedia = useMemo(() => {
        return importedMedia
            .filter(item => mediaFilterType === 'all' || item.type === mediaFilterType)
            .filter(item => mediaFilterCanvas === 'all' || item.canvasName === mediaFilterCanvas);
    }, [importedMedia, mediaFilterType, mediaFilterCanvas]);

    const filteredGeneratedMedia = useMemo(() => {
        return generatedMedia
            .filter(item => mediaFilterType === 'all' || item.type === mediaFilterType)
            .filter(item => mediaFilterCanvas === 'all' || item.canvasName === mediaFilterCanvas);
    }, [generatedMedia, mediaFilterType, mediaFilterCanvas]);

    const filteredInspirationMedia = useMemo(() => {
        return inspirationMedia
            .filter(item => inspirationFolderFilter === 'all' || item.folder === inspirationFolderFilter);
    }, [inspirationMedia, inspirationFolderFilter]);

    const importedSentinelRef = useInfiniteScroll(fetchNextUploads, hasMoreUploads, isFetchingMoreUploads);
    const generatedSentinelRef = useInfiniteScroll(fetchNextGenerations, hasMoreGenerations, isFetchingMoreGenerations);
    const inspirationSentinelRef = useInfiniteScroll(fetchNextInspiration, hasMoreInspiration, isFetchingMoreInspiration);

    // Save node data
    const saveNodeData = useCallback(async (id, partialData) => {
        const node = getNodes().find(n => n.id === id);
        if (!node) return;

        const fullData = { ...node.data, ...partialData };
        // Remove onSave and onResize from data before sending to server
        const { onSave, onResize, ...dataToSave } = fullData;

        setIsSaving(true);
        try {
            await updateNode({
                nodeId: id,
                data: {
                    data: dataToSave,
                    system: { id: 'system', email: 'system@pxlplygrnd.com' }
                }
            });
        } catch (error) {
            console.error('Failed to update node data:', error);
        }
        setIsSaving(false);
    }, [getNodes, updateNode]);

    // Save node resize
    const saveNodeResize = useCallback(async (id, dimensions) => {
        setIsSaving(true);
        try {
            await updateNode({
                nodeId: id,
                data: {
                    width: dimensions.width,
                    height: dimensions.height,
                    // Also save position as resizing might change it (e.g. resizing from top-left)
                    position_x: dimensions.x,
                    position_y: dimensions.y,
                    system: { id: 'system', email: 'system@pxlplygrnd.com' }
                }
            });
        } catch (error) {
            console.error('Failed to update node dimensions:', error);
        }
        setIsSaving(false);
    }, [updateNode]);

    // Transform DB nodes to ReactFlow format
    const transformedNodes = useMemo(() => {
        if (!dbNodes || dbNodes.length === 0 && !nodesLoading) return [];

        // Create a map of nodeId -> groupId
        const nodeParentMap = new Map();
        if (nodeGroups && nodeGroups.length > 0) {
            nodeGroups.forEach(group => {
                if (group.nodes && Array.isArray(group.nodes)) {
                    group.nodes.forEach(nodeId => {
                        nodeParentMap.set(nodeId, group.id);
                    });
                }
            });
        }

        const rfNodes = dbNodes.map(node => ({
            id: node.id,
            type: node.type,
            position: { x: node.position_x || 0, y: node.position_y || 0 },
            data: { ...(node.data || {}), canvasId, onSave: saveNodeData, onResize: saveNodeResize },
            style: { width: node.width || 200, height: node.height || 200 },
            parentNode: nodeParentMap.get(node.id) || undefined, // Derive parent from group membership
            extent: nodeParentMap.has(node.id) ? 'parent' : undefined // Constrain to parent if grouped
        }));

        // Merge with groups if any
        if (nodeGroups.length > 0) {
            const groupNodes = nodeGroups.map(group => ({
                id: group.id,
                type: 'group',
                position: { x: group.position_x || 0, y: group.position_y || 0 },
                data: {
                    label: group.group_label,
                    onSave: (id, data) => {
                        // Map label back to group_label for API
                        const updateData = { ...data };
                        if (updateData.label) {
                            updateData.group_label = updateData.label;
                            delete updateData.label;
                        }
                        updateNodeGroup({ groupId: id, data: updateData });
                    }
                },
                style: {
                    width: group.width || 200,
                    height: group.height || 200,
                    backgroundColor: 'transparent',
                    border: 'none'
                },
                zIndex: -1
            }));

            // Filter out any potential duplicates by ID just in case
            const existingIds = new Set(rfNodes.map(n => n.id));
            const newGroups = groupNodes.filter(g => !existingIds.has(g.id));
            return [...rfNodes, ...newGroups];
        }

        return rfNodes;
    }, [dbNodes, nodeGroups, nodesLoading, canvasId, saveNodeData, saveNodeResize, updateNodeGroup]);

    // Apply transformed nodes to ReactFlow state
    // We use a simplified comparison to avoid infinite loops if objects are technically new but content is same
    // However, for now, we trust useMemo above.
    // Ideally we only setNodes if the LENGTH or key properties changed, or on initial load.
    // But ReactFlow's setNodes is creating new references which triggers this effect again if we depended on 'nodes'
    // We DO NOT depend on 'nodes' state here, so no loop from that.
    useEffect(() => {
        if (transformedNodes.length > 0) {
            setNodes(prevNodes => {
                // Build a map of previous selection states
                const selectionMap = new Map();
                prevNodes.forEach(n => {
                    if (n.selected) {
                        selectionMap.set(n.id, true);
                    }
                });

                // Optimization: Lightweight check instead of JSON.stringify
                if (prevNodes.length !== transformedNodes.length) {
                    // Preserve selection state when replacing nodes
                    return transformedNodes.map(t => ({
                        ...t,
                        selected: selectionMap.get(t.id) || false
                    }));
                }

                // Check for structural or content changes with robust comparison
                let hasChanged = false;
                for (let i = 0; i < prevNodes.length; i++) {
                    const p = prevNodes[i];
                    const t = transformedNodes[i];

                    // 1. Basic properties update
                    if (p.id !== t.id ||
                        p.type !== t.type ||
                        p.position.x !== t.position.x ||
                        p.position.y !== t.position.y ||
                        p.style?.width !== t.style?.width ||
                        p.style?.height !== t.style?.height) {
                        hasChanged = true;
                        break;
                    }

                    // 2. Data comparison (using JSON.stringify to safely ignore functions like onSave/onResize and handle deep equality)
                    // This is O(1) per node relative to the total state size, and prevents reference-inequality loops
                    if (JSON.stringify(p.data) !== JSON.stringify(t.data)) {
                        hasChanged = true;
                        break;
                    }
                }

                if (hasChanged) {
                    // Preserve selection state when updating nodes
                    return transformedNodes.map(t => ({
                        ...t,
                        selected: selectionMap.get(t.id) || false
                    }));
                }
                return prevNodes;
            });
        }
    }, [transformedNodes, setNodes]);

    // Transform DB edges to ReactFlow format
    useEffect(() => {
        if (dbEdges.length > 0 || !edgesLoading) {
            const rfEdges = dbEdges.map(edge => {
                // Uniform neutral edge color for all connections
                const edgeColor = '#8b949e';

                return {
                    id: edge.id,
                    source: edge.source_node_id,
                    target: edge.target_node_id,
                    sourceHandle: edge.source_handle_id || null,
                    targetHandle: edge.target_handle_id || null,
                    animated: edge.animated || false,
                    style: { stroke: edgeColor, strokeWidth: 4 },
                };
            });
            setEdges(rfEdges);
        }
    }, [dbEdges, edgesLoading, setEdges, dbNodes]); // Added dbNodes to dependency

    // Complete the connection (called directly or after slot selection)
    const completeConnection = useCallback(async (params) => {
        setIsSaving(true);
        try {
            const edgeData = {
                source_node_id: params.source,
                target_node_id: params.target,
                source_handle_id: params.sourceHandle || '',
                target_handle_id: params.targetHandle || '',
                animated: false,
                system: { id: 'system', email: 'system@pxlplygrnd.com' }
            };
            const newEdge = await createEdgeMutation.mutateAsync(edgeData);

            // Uniform neutral edge color for all connections
            const edgeColor = '#8b949e';

            setEdges((eds) => addEdge({
                ...params,
                id: newEdge.id,
                style: { stroke: edgeColor, strokeWidth: 4 },
            }, eds));

            // --- Node Initialization Logic ---
            // If connecting TO an imageSlicer, check source and initialize
            const targetNode = getNodes().find(n => n.id === params.target);
            if (targetNode && targetNode.type === 'imageSlicer') {
                const sourceNode = getNodes().find(n => n.id === params.source);
                if (sourceNode) {
                    // Determine source image URL
                    let src = sourceNode.data?.src;
                    if (!src && sourceNode.data?.text) {
                        // Keep text? No, slicer needs image.
                        // If source is text node, maybe we don't init.
                    }

                    if (src) {
                        const updates = { src };
                        // Initialize defaults if not present
                        if (!targetNode.data.mode) updates.mode = 'even';
                        if (!targetNode.data.horizontalLines) updates.horizontalLines = [0.5];
                        if (!targetNode.data.verticalLines) updates.verticalLines = [0.5];

                        setNodes((nds) => nds.map((n) => {
                            if (n.id === params.target) {
                                return { ...n, data: { ...n.data, ...updates } };
                            }
                            return n;
                        }));

                        // Persist
                        saveNodeData(params.target, updates);
                    }
                }
            }
            // ---------------------------------

        } catch (error) {
            console.error('Failed to create edge:', error);
        }
        setIsSaving(false);
    }, [createEdgeMutation, setEdges, getNodes, setNodes, saveNodeData]);

    // Handle edge connections - save to DB with auto-detect routing
    const onConnect = useCallback(async (params) => {
        const sourceNode = getNodes().find(n => n.id === params.source);
        const targetNode = getNodes().find(n => n.id === params.target);
        const currentEdges = edges;

        if (!sourceNode || !targetNode) {
            await completeConnection(params);
            return;
        }

        // Determine source type
        const isTextSource = (
            sourceNode.type === 'text' ||
            sourceNode.type === 'textSlicer' ||
            sourceNode.type === 'moondreamQuery'
        );

        const isImageSource = (
            sourceNode.type === 'media' ||
            (sourceNode.type === 'qwen' && sourceNode.data?.src) ||
            sourceNode.type === 'midjourney' ||
            sourceNode.type === 'marigoldDepth' ||
            sourceNode.type === 'segmentation' ||
            sourceNode.type === 'hunyuanPanorama' ||
            sourceNode.type === 'imageSlicer' ||
            sourceNode.type === 'topaz'
        );

        const isVideoSource = sourceNode.type === 'video' || sourceNode.type === 'veo';
        const isAudioSource = sourceNode.type === 'textToSpeech';

        // Define input slot configurations for target nodes
        const NODE_INPUT_SLOTS = {
            // MediaNode (Image Generator) - text for prompt, image for reference
            media: {
                text: 'left-top',
                image: ['left-bottom']
            },
            // VideoNode (Kling)
            video: {
                text: 'left-top',
                image: ['left-middle', 'left-bottom'] // Start Frame, End Frame
            },
            // VeoNode
            veo: {
                text: 'left-input',
                image: ['left-input']
            },
            // SegmentationNode
            segmentation: {
                text: 'left-top',
                image: ['left-bottom']
            },
            // MidjourneyNode - only accepts text prompts (no image input)
            midjourney: {
                text: 'left-top'
            },
            // QwenNode - only accepts images (for 3D editing)
            qwen: {
                image: ['left-input']
            },
            // Hunyuan3DNode
            hunyuan3d: {
                image: ['input-front', 'input-back', 'input-left', 'input-right']
            },
            // MoondreamQueryNode
            moondreamQuery: {
                text: 'left-top',
                image: ['left-bottom']
            },
            // ColorAnalysisNode
            colorAnalysis: {
                image: ['image']
            },
            // MarigoldNode
            marigoldDepth: {
                image: ['left-input']
            },
            // TextToSpeechNode
            textToSpeech: {
                text: 'left-input'
            },
            // ImageSlicingNode
            imageSlicer: {
                image: ['input-image']
            },
            // TextSlicingNode
            textSlicer: {
                text: 'input-text'
            },
            // HunyuanPanoramaNode
            hunyuanPanorama: {
                text: 'left-input'
            },
            // DynamicTextNode - accepts multiple text inputs to single handle
            dynamicText: {
                text: 'left-input'
            }
        };

        const targetConfig = NODE_INPUT_SLOTS[targetNode.type];

        // Auto-route based on source type - ALWAYS override user's drag target
        let targetHandle = null;

        if (targetConfig) {
            if (isTextSource) {
                // Text MUST go to text slot only
                if (targetConfig.text) {
                    targetHandle = targetConfig.text;
                } else {
                    // This node doesn't accept text input - don't create connection
                    console.warn('Cannot connect text to this node type');
                    return;
                }
            } else if (isImageSource) {
                // Special case: VideoNode needs slot selection for Start Frame vs End Frame
                if (targetNode.type === 'video' && targetConfig.image && targetConfig.image.length > 1) {
                    setPendingConnection(params);
                    setAvailableSlots([
                        { id: 'left-middle', label: 'Start Frame', description: 'First frame of the video', required: true },
                        { id: 'left-bottom', label: 'End Frame', description: 'Last frame of the video (optional)', required: false }
                    ]);
                    setSlotSelectionVisible(true);
                    return; // Wait for user selection
                }

                // Image goes to first available image slot
                if (targetConfig.image) {
                    const imageSlots = targetConfig.image;
                    const usedSlots = currentEdges
                        .filter(e => e.target === targetNode.id)
                        .map(e => e.targetHandle);

                    const availableSlot = imageSlots.find(slot => !usedSlots.includes(slot));
                    targetHandle = availableSlot || imageSlots[0];
                } else {
                    // This node doesn't accept image input
                    console.warn('Cannot connect image to this node type');
                    return;
                }
            } else if (isVideoSource && targetConfig.video) {
                targetHandle = targetConfig.video;
            } else if (isAudioSource && targetConfig.audio) {
                targetHandle = targetConfig.audio;
            }
        }

        // If no targetHandle was determined, use whatever ReactFlow provided
        if (!targetHandle) {
            targetHandle = params.targetHandle;
        }

        // Complete connection with auto-detected handle
        const paramsWithSlot = {
            ...params,
            targetHandle: targetHandle || params.targetHandle
        };
        await completeConnection(paramsWithSlot);
    }, [getNodes, edges, completeConnection]);

    // Handle slot selection
    const handleSlotSelect = useCallback(async (slotId) => {
        if (pendingConnection) {
            const paramsWithSlot = {
                ...pendingConnection,
                targetHandle: slotId
            };
            await completeConnection(paramsWithSlot);
        }
        setSlotSelectionVisible(false);
        setPendingConnection(null);
        setAvailableSlots([]);
    }, [pendingConnection, completeConnection]);

    // Quick-Connect: Double-click node to select as source
    const onNodeDoubleClick = useCallback((event, node) => {
        // Disable when fullscreen preview is active
        if (previewVisible) return;

        // If double-clicking same node, cancel quick-connect mode
        if (quickConnectSourceId === node.id) {
            setQuickConnectSourceId(null);
            message.info('Quick-connect cancelled');
            return;
        }
        // Set as source node
        setQuickConnectSourceId(node.id);
        message.info('Click another node to connect');
    }, [quickConnectSourceId, previewVisible]);

    // Quick-Connect: Click node to complete connection
    const onNodeClick = useCallback(async (event, node) => {
        // Only act if quick-connect mode is active
        if (!quickConnectSourceId) return;

        // Stop event from bubbling to node internals (e.g., fullscreen preview)
        event.stopPropagation();

        // Prevent connecting to self
        if (quickConnectSourceId === node.id) return;

        // Create connection using existing onConnect with auto-routing
        const params = {
            source: quickConnectSourceId,
            target: node.id,
            sourceHandle: null,
            targetHandle: null
        };

        await onConnect(params);

        // Exit quick-connect mode
        setQuickConnectSourceId(null);
    }, [quickConnectSourceId, onConnect]);

    // Quick-Connect: Click pane to cancel
    const onPaneClick = useCallback(() => {
        if (quickConnectSourceId) {
            setQuickConnectSourceId(null);
            message.info('Quick-connect cancelled');
        }
    }, [quickConnectSourceId]);

    // Quick-Connect: Visual feedback effect - apply CSS class to source node and canvas
    useEffect(() => {
        // Remove class from any previously highlighted node
        const prevHighlighted = document.querySelector('.react-flow__node.quick-connect-source');
        if (prevHighlighted) {
            prevHighlighted.classList.remove('quick-connect-source');
        }

        // Toggle quick-connect-active class on wrapper to block internal node clicks
        if (reactFlowWrapper.current) {
            if (quickConnectSourceId) {
                reactFlowWrapper.current.classList.add('quick-connect-active');
            } else {
                reactFlowWrapper.current.classList.remove('quick-connect-active');
            }
        }

        // Add class to current source node
        if (quickConnectSourceId) {
            const sourceNode = document.querySelector(`[data-id="${quickConnectSourceId}"]`);
            if (sourceNode) {
                sourceNode.classList.add('quick-connect-source');
            }
        }

        // Cleanup on unmount
        return () => {
            const highlighted = document.querySelector('.react-flow__node.quick-connect-source');
            if (highlighted) {
                highlighted.classList.remove('quick-connect-source');
            }
            if (reactFlowWrapper.current) {
                reactFlowWrapper.current.classList.remove('quick-connect-active');
            }
        };
    }, [quickConnectSourceId]);

    // Handle edge deletion
    const onEdgeClick = useCallback(async (event, edge) => {
        if (activeTool === 'cut') {
            setIsSaving(true);
            try {
                await deleteEdgeMutation.mutateAsync(edge.id);
                setEdges((eds) => eds.filter((e) => e.id !== edge.id));
            } catch (error) {
                console.error('Failed to delete edge:', error);
            }
            setIsSaving(false);
        }
    }, [activeTool, deleteEdgeMutation, setEdges]);

    // Handle node position changes
    const handleNodesChange = useCallback((changes) => {
        // Filter out group deletions from immediate application to prevent crash
        const immediateChanges = changes.filter(change => {
            if (change.type === 'remove') {
                const node = nodes.find(n => n.id === change.id);
                return !(node && node.type === 'group');
            }
            return true;
        });

        onNodesChange(immediateChanges);

        // Handle node removal
        changes.forEach(async (change) => {
            if (change.type === 'remove') {
                const node = nodes.find(n => n.id === change.id);
                if (node && node.type === 'group') {
                    // Handle Group Deletion (Ungroup children)
                    try {
                        const groupX = node.position.x || 0;
                        const groupY = node.position.y || 0;

                        // Find children to ungroup
                        const children = nodes.filter(n => n.parentNode === node.id);

                        // 1. Update backend (convert to absolute positions)
                        await Promise.all(children.map(async (child) => {
                            const absX = groupX + (child.position.x || 0);
                            const absY = groupY + (child.position.y || 0);

                            await updateNode({
                                nodeId: child.id,
                                data: {
                                    position_x: absX,
                                    position_y: absY,
                                    // By not sending parentNode, backend/frontend will treat as root
                                    system: { id: 'system', email: 'system@pxlplygrnd.com' }
                                }
                            });
                        }));

                        // 2. Update local state to remove parentNode from children prevents crash
                        // We do this BEFORE removing the group node from local state (via query invalidation or manual setNodes)
                        setNodes((nds) => nds.map((n) => {
                            if (n.parentNode === node.id) {
                                return {
                                    ...n,
                                    parentNode: undefined,
                                    position: {
                                        x: groupX + n.position.x,
                                        y: groupY + n.position.y
                                    }
                                };
                            }
                            return n;
                        }));

                        // 3. Delete the group from backend
                        await deleteNodeGroupMutation.mutateAsync(change.id);

                        // 4. Finally remove group from local state manually to ensure it disappears
                        setNodes((nds) => nds.filter(n => n.id !== change.id));

                        message.success('Group deleted, nodes preserved');
                    } catch (error) {
                        console.error('Failed to delete group:', error);
                        message.error('Failed to delete group');
                    }
                } else {
                    // Regular Node Deletion
                    try {
                        await deleteNodeMutation.mutateAsync(change.id);
                    } catch (error) {
                        console.error('Failed to delete node:', error);
                    }
                }
            }
        });
    }, [onNodesChange, deleteNodeMutation, deleteNodeGroupMutation, nodes, updateNode, setNodes]);

    // Performance: handlers for pan/zoom interaction state
    // Using direct DOM manipulation instead of React state for instant response (no render delay)
    const onMoveStart = useCallback(() => {
        if (reactFlowWrapper.current) {
            reactFlowWrapper.current.classList.add('canvas-interacting');
        }
    }, []);

    const onMoveEnd = useCallback(() => {
        if (reactFlowWrapper.current) {
            reactFlowWrapper.current.classList.remove('canvas-interacting');
        }
    }, []);

    // Track drag start position to avoid saving if node didn't move
    const dragStartPositions = useRef({});

    const onNodeDragStart = useCallback((event, node) => {
        // Track all selected nodes, plus the dragged one
        const nodes = getNodes();
        const selectedNodes = nodes.filter(n => n.selected || n.id === node.id);

        selectedNodes.forEach(n => {
            dragStartPositions.current[n.id] = { x: n.position.x, y: n.position.y };
        });
    }, [getNodes]);

    // Save position when drag ends

    // Helper to save multiple nodes
    const saveNodes = useCallback(async (nodesToSave) => {
        if (!nodesToSave || nodesToSave.length === 0) return;

        setIsSaving(true);
        await Promise.all(nodesToSave.map(async (n) => {
            try {
                if (n.type === 'group') {
                    // Update the group itself
                    await updateNodeGroup({
                        groupId: n.id,
                        data: {
                            position_x: n.position.x,
                            position_y: n.position.y,
                            width: n.style.width,
                            height: n.style.height,
                            system: { id: 'system', email: 'system@pxlplygrnd.com' }
                        }
                    });

                    // Update all children of this group to ensure they stay in sync
                    const childNodes = getNodes().filter(child => child.parentNode === n.id);
                    await Promise.all(childNodes.map(async (child) => {
                        await updateNode({
                            nodeId: child.id,
                            data: {
                                position_x: child.position.x,
                                position_y: child.position.y,
                                // DO NOT send parentNode to API
                                system: { id: 'system', email: 'system@pxlplygrnd.com' }
                            }
                        });
                    }));

                } else {
                    // Regular node update
                    await updateNodeMutation.mutateAsync({
                        nodeId: n.id,
                        data: {
                            position_x: n.position.x,
                            position_y: n.position.y,
                            // DO NOT send parentNode to API even if it exists in local state
                            system: { id: 'system', email: 'system@pxlplygrnd.com' }
                        }
                    });
                }
            } catch (error) {
                console.error(`Failed to update node position for ${n.id}:`, error);
            }
        }));
        setIsSaving(false);
    }, [updateNodeMutation, updateNodeGroup, getNodes, updateNode]);

    // Handle selection drag stop (for multi-node moves)
    const onSelectionDragStop = useCallback((event, selectionNodes) => {
        saveNodes(selectionNodes);
    }, [saveNodes]);

    // Handle single node drag stop
    const onNodeDragStop = useCallback((event, node, draggedNodes) => {
        // If draggedNodes has more than 1 node, it's a selection drag
        // We let onSelectionDragStop handle that to avoid double saves
        if (draggedNodes && draggedNodes.length > 1) {
            return;
        }

        // Otherwise, save the single node (fallback or single drag)
        saveNodes([node]);

        // Clear legacy tracking if it exists
        dragStartPositions.current = {};
    }, [saveNodes]);

    // Drag and Drop handlers
    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(async (event) => {
        event.preventDefault();

        const mediaData = event.dataTransfer.getData('application/json');
        if (!mediaData) return;

        const item = JSON.parse(mediaData);
        const position = screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
        });

        const defaultWidth = 1200; // Increased size (3x previous)
        const defaultHeight = 1320; // Increased size (3x previous)

        // Find a non-overlapping position near the drop location
        const safePosition = findNonOverlappingPosition(
            nodes,
            position,
            defaultWidth,
            defaultHeight
        );

        setIsSaving(true);
        try {
            if (item.type === 'model') {
                // Create Hunyuan 3D Node for models
                const nodeData = {
                    type: 'hunyuan3d',
                    position_x: safePosition.x,
                    position_y: safePosition.y,
                    width: 600, // 3D viewer default size
                    height: 600,
                    data: {
                        modelUrl: item.src, // Set model URL
                        src: item.src,
                        nodeTitle: item.label || '3D Model',
                    },
                    system: { id: 'system', email: 'system@pxlplygrnd.com' }
                };
                const newNode = await createNodeMutation.mutateAsync(nodeData);

                setNodes((nds) => nds.concat({
                    id: newNode.id,
                    type: 'hunyuan3d',
                    position: safePosition,
                    data: nodeData.data,
                    style: { width: 600, height: 600 }
                }));
            } else {
                // Default Media Node creation (Images/Videos)
                const nodeData = {
                    type: 'media',
                    position_x: safePosition.x,
                    position_y: safePosition.y,
                    width: defaultWidth,
                    height: defaultHeight,
                    data: {
                        label: 'Upload',
                        src: item.src,
                        uploading: false,
                        nodeTitle: 'Upload',
                        iconType: 'cloud-sync',
                        nodeColor: '#1890ff',
                        isUpload: true
                    },
                    system: { id: 'system', email: 'system@pxlplygrnd.com' }
                };
                const newNode = await createNodeMutation.mutateAsync(nodeData);

                setNodes((nds) => nds.concat({
                    id: newNode.id,
                    type: 'media',
                    position: safePosition,
                    data: nodeData.data,
                    style: { width: defaultWidth, height: defaultHeight }
                }));
            }
        } catch (error) {
            console.error('Failed to create node:', error);
        }
        setIsSaving(false);
    }, [screenToFlowPosition, createNodeMutation, setNodes, nodes]);

    const handleCopyPromptToCanvas = async (content, type) => {
        // Calculate center of viewport
        let position = { x: 250, y: 250 };
        if (reactFlowWrapper.current) {
            const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
            const center = screenToFlowPosition({ x: width / 2, y: height / 2 });
            // Jitter
            position = {
                x: center.x + (Math.random() - 0.5) * 40,
                y: center.y + (Math.random() - 0.5) * 40
            };
        }

        // Find safe position
        const safePosition = findNonOverlappingPosition(nodes, position, 1200, 600);

        setIsSaving(true);
        try {
            const nodeData = {
                type: 'text',
                position_x: safePosition.x,
                position_y: safePosition.y,
                width: 1200,
                height: 600,
                data: {
                    label: type === 'system' ? 'System Prompt' : 'Prompt',
                    text: content,
                    nodeTitle: type === 'system' ? 'System Prompt' : 'Saved Prompt',
                    iconType: type === 'system' ? 'robot' : 'file-text',
                    nodeColor: type === 'system' ? '#722ed1' : '#fa8c16'
                },
                system: { id: 'system', email: 'system@pxlplygrnd.com' }
            };

            const newNode = await createNodeMutation.mutateAsync(nodeData);

            setNodes((nds) => nds.concat({
                id: newNode.id,
                type: 'text',
                position: safePosition,
                data: nodeData.data,
                style: { width: 1200, height: 600 }
            }));

            message.success('Prompt added to canvas');
        } catch (error) {
            console.error('Failed to add prompt node:', error);
            message.error('Failed to add prompt to canvas');
        }
        setIsSaving(false);
    };

    const handleAddMediaToCanvas = async (mediaItem) => {
        // Calculate center of viewport
        let position = { x: 250, y: 250 };
        if (reactFlowWrapper.current) {
            const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
            const center = screenToFlowPosition({ x: width / 2, y: height / 2 });
            position = {
                x: center.x + (Math.random() - 0.5) * 40,
                y: center.y + (Math.random() - 0.5) * 40
            };
        }

        const defaultWidth = 1200;
        const defaultHeight = 1320;

        const safePosition = findNonOverlappingPosition(
            nodes,
            position,
            defaultWidth,
            defaultHeight
        );

        setIsSaving(true);
        try {
            const nodeData = {
                type: 'media',
                position_x: safePosition.x,
                position_y: safePosition.y,
                width: defaultWidth,
                height: defaultHeight,
                data: {
                    label: 'Upload',
                    src: mediaItem.src || mediaItem.url,
                    uploading: false,
                    nodeTitle: 'Upload',
                    iconType: 'cloud-sync',
                    nodeColor: '#1890ff',
                    isUpload: true
                },
                system: { id: 'system', email: 'system@pxlplygrnd.com' }
            };

            const newNode = await createNodeMutation.mutateAsync(nodeData);

            setNodes((nds) => nds.concat({
                id: newNode.id,
                type: 'media',
                position: safePosition,
                data: nodeData.data,
                style: { width: defaultWidth, height: defaultHeight }
            }));
            message.success('Image added to canvas');
        } catch (error) {
            console.error('Failed to add media node:', error);
            message.error('Failed to add to canvas');
        }
        setIsSaving(false);
    };

    const addNode = async (type, subType) => {
        // Calculate center of viewport
        let preferredPosition = { x: 250, y: 250 };
        if (reactFlowWrapper.current) {
            const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
            // Get center of view as a starting point
            const center = screenToFlowPosition({
                x: width / 2,
                y: height / 2
            });

            // Add a small random jitter to preferred position so perfectly centered adds don't stack perfectly before calculation
            preferredPosition = {
                x: center.x + (Math.random() - 0.5) * 40,
                y: center.y + (Math.random() - 0.5) * 40
            };
        }

        let data = { label: 'New Node', nodeTitle: 'Node', iconType: 'default', nodeColor: '#1890ff' };

        // Increased default sizes (approx 3x previous)
        let width = 1200, height = 1320;

        if (type === 'text') {
            if (subType === 'ai-text') {
                data = { label: 'AI Text', text: 'Generating content...', nodeTitle: 'Text Generator', iconType: 'font-size', nodeColor: '#52c41a' };
            } else {
                data = { label: 'Note', text: 'Type your note here...', nodeTitle: 'Plain Text Node', iconType: 'file-text', nodeColor: '#fa8c16' };
            }
            // Increased text node size
            width = 1200; height = 600;
        } else if (type === 'media') {
            if (subType === 'upload') {
                data = { label: 'Upload', uploading: true, progress: 0, nodeTitle: 'Upload', iconType: 'cloud-sync', nodeColor: '#1890ff', isUpload: true };
            } else {
                data = { label: 'Generated Image', src: 'https://via.placeholder.com/150', uploading: false, nodeTitle: 'Image Generator', iconType: 'picture', nodeColor: '#722ed1' };
            }
        } else if (type === 'video') {
            data = { label: 'AI Video', nodeTitle: 'Video Generator', iconType: 'video-camera', nodeColor: '#f5222d' };
            width = 1920; height = 1200; // Increased video size
        } else if (type === 'code') {
            data = { label: 'API Integration', code: '// Fetch external data\nfetch("https://api.example.com/data")\n  .then(res => res.json())\n  .then(data => console.log(data));', nodeTitle: 'API Node', iconType: 'api', nodeColor: '#fadb14' };
            width = 1680; height = 900; // Increased code size
        } else if (type === 'qwen') {
            data = { label: 'Qwen 3D', nodeTitle: 'Qwen 3D Edit', iconType: 'gateway', nodeColor: '#eb2f96' };
            width = 1200; height = 800;
        } else if (type === 'imageSlicer') {
            data = { label: 'Image Slicer', nodeTitle: 'Image Slicer', iconType: 'scissor', nodeColor: '#ff4d4f' };
            width = 800; height = 800;
        } else if (type === 'textSlicer') {
            data = { label: 'Text Slicer', nodeTitle: 'Text Slicer', iconType: 'scissor', nodeColor: '#eb2f96', pattern: ':-' };
            width = 400; height = 400;
        } else if (type === 'moodboard') {
            data = { label: 'Moodboard', nodeTitle: 'Moodboard', iconType: 'appstore', nodeColor: '#ffec3d', backgroundColor: '#161b22', mediaItems: [] };
            width = 800; height = 800;
        } else if (type === 'textToSpeech') {
            data = { label: 'Text to Speech', nodeTitle: 'Text to Speech', iconType: 'sound', nodeColor: '#eb2f96' };
            width = 300; height = 250;
        } else if (type === 'midjourney') {
            data = { label: 'Midjourney', nodeTitle: 'Midjourney', iconType: 'fire', nodeColor: '#ffffff', ar: '16:9', v: '6.1' };
            width = 600; height = 600;
        } else if (type === 'segmentation') {
            data = { label: 'Segmentation', nodeTitle: 'Segmentation', iconType: 'scan', nodeColor: '#13c2c2' };
            width = 800; height = 800;
        } else if (type === 'octalysis') {
            data = { label: 'Octalysis', nodeTitle: 'Octalysis', iconType: 'deployment-unit', nodeColor: '#faad14' };
            width = 1000; height = 800;
        } else if (type === 'colorAnalysis') {
            data = { label: 'Color Analysis', nodeTitle: 'Color Analysis', iconType: 'bg-colors', nodeColor: '#eb2f96' };
            width = 600; height = 600;
        } else if (type === 'topaz') {
            data = { label: 'Topaz Upscale', nodeTitle: 'Topaz Upscale', iconType: 'picture', nodeColor: '#13c2c2' };
            width = 600; height = 800;
        } else if (type === 'dynamicText') {
            data = { label: 'Dynamic Text', template: '', nodeTitle: 'Dynamic Text Builder', iconType: 'merge-cells', nodeColor: '#13c2c2' };
            width = 500; height = 400;
        }

        // Find non-overlapping position
        const position = findNonOverlappingPosition(
            nodes,
            preferredPosition,
            width,
            height
        );

        setAddNodeMenuOpen(false);
        setIsSaving(true);

        try {
            const nodeData = {
                type,
                position_x: position.x,
                position_y: position.y,
                width,
                height,
                data,
                system: { id: 'system', email: 'system@pxlplygrnd.com' }
            };
            const newNode = await createNodeMutation.mutateAsync(nodeData);

            setNodes((nds) => nds.concat({
                id: newNode.id,
                type,
                position,
                data: { ...data, onSave: saveNodeData, onResize: saveNodeResize },
                style: { width, height }
            }));
        } catch (error) {
            console.error('Failed to create node:', error);
        }
        setIsSaving(false);
    };

    // Group Nodes Logic
    const handleGroupNodes = async () => {
        const selectedNodes = getNodes().filter(n => n.selected);
        if (selectedNodes.length < 2) {
            message.warning('Select at least 2 nodes to group');
            return;
        }

        // Calculate bounding box
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        selectedNodes.forEach(node => {
            minX = Math.min(minX, node.position.x);
            minY = Math.min(minY, node.position.y);
            maxX = Math.max(maxX, node.position.x + (node.width || 200));
            maxY = Math.max(maxY, node.position.y + (node.height || 200));
        });

        const width = maxX - minX + 160; // Increased padding (80 left + 80 right)
        const height = maxY - minY + 160; // Increased padding
        const groupX = minX - 80;
        const groupY = minY - 80; // Extra space for header/margin

        try {
            // 1. Create the group
            const newGroup = await createNodeGroupMutation.mutateAsync({
                canvas_id: canvasId,
                group_label: 'New Group',
                nodes: selectedNodes.map(n => n.id),
                position_x: groupX,
                position_y: groupY,
                width,
                height,
                color: 'rgba(255, 255, 255, 0.1)'
            });

            // 2. Update all selected nodes to be children of the group
            // We update their positions to be relative to the group, but we DO NOT save parentNode to DB
            await Promise.all(selectedNodes.map(async (node) => {
                const relativeX = node.position.x - groupX;
                const relativeY = node.position.y - groupY;

                await updateNode({
                    nodeId: node.id,
                    data: {
                        position_x: relativeX,
                        position_y: relativeY,
                        // parentNode: newGroup.id, // REMOVED: Do not persist parent node
                        // extent: 'parent', // REMOVED from DB save, but handled in local state by effect
                        system: { id: 'system', email: 'system@pxlplygrnd.com' }
                    }
                });
            }));

            // 3. Force refresh logic is handled by query invalidation, 
            // but we might want to optimistically update local state for smoothness
            // However, React Flow handles parent/child via passing 'nodes' prop.
            // Our useEffect hook [dbNodes, nodeGroups] will re-trigger and rebuild the node list properly.

            message.success('Nodes grouped successfully');
        } catch (error) {
            console.error('Failed to group nodes:', error);
            message.error('Failed to group nodes');
        }
    };


    // Save canvas name
    const handleNameSave = async () => {
        if (!editingName.trim() || editingName === canvas?.name) {
            setIsEditingName(false);
            return;
        }

        try {
            await updateCanvasMutation.mutateAsync({
                canvasId,
                data: {
                    name: editingName,
                    system: { id: 'system', email: 'system@pxlplygrnd.com' }
                }
            });
            message.success('Canvas renamed');
        } catch (error) {
            console.error('Failed to rename canvas:', error);
            message.error('Failed to rename canvas');
        } finally {
            setIsEditingName(false);
        }
    };

    // Initialize editing name when entering edit mode
    const startEditingName = () => {
        setEditingName(canvas?.name || '');
        setIsEditingName(true);
    };

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Skip shortcuts if typing in input fields or if scrapbook is open
            if (['INPUT', 'TEXTAREA'].includes(event.target.tagName)) return;
            if (scrapbookOpen || glyphEditorOpen) return; // Don't trigger editor shortcuts when panels are open

            switch (event.key.toLowerCase()) {
                case 'v':
                    setActiveTool('select');
                    break;
                case 'h':
                    setActiveTool('pan');
                    break;
                case 's':
                    setActiveTool('cut');
                    break;
                case ' ':
                    event.preventDefault();
                    setAddNodeMenuOpen((prev) => !prev);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [scrapbookOpen, glyphEditorOpen]);

    const goBack = () => navigate('/dashboard');

    const isLoading = canvasLoading || nodesLoading || edgesLoading;

    if (isLoading) {
        return (
            <Layout style={{ height: '100vh', background: '#0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />} />
                    <Text style={{ display: 'block', marginTop: 16, color: '#8b949e' }}>Loading canvas...</Text>
                </div>
            </Layout>
        );
    }

    // Determine if this is a UI Design canvas (for conditional content rendering)
    const isUIDesignCanvas = canvas?.canvasType === 'uiDesign';

    return (
        <Layout style={{ height: '100vh', background: '#0e1117' }}>
            {/* Dark Top Navigation */}
            <Header style={{
                background: '#161b22',
                padding: '0 24px',
                borderBottom: '1px solid #30363d',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: 64,
                zIndex: 10,
                lineHeight: 'normal'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, overflow: 'hidden' }}>
                    {/* Logo */}
                    <Logo
                        style={{ width: 24, height: 31, cursor: 'pointer', flexShrink: 0 }}
                        onClick={() => navigate('/dashboard')}
                    />
                    {/* Open Canvases as inline items */}
                    {openTabs.map((tabId) => {
                        const isActive = tabId === canvasId;
                        const tabCanvas = tabId === canvasId ? canvas : allCanvases.find(c => c.id === tabId);
                        const tabName = tabCanvas?.name || 'Untitled';
                        const isUITab = tabCanvas?.canvasType === 'uiDesign';

                        return (

                            <div key={tabId} style={{ display: 'flex', alignItems: 'center' }}>
                                <div
                                    onClick={() => {
                                        if (!isActive) {
                                            navigate(`/canvas/${tabId}`);
                                        }
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        cursor: isActive ? 'default' : 'pointer',
                                        padding: '4px 8px',
                                        borderRadius: 6,
                                        background: isActive ? 'transparent' : 'transparent',
                                        transition: 'background 0.2s',
                                        flexShrink: 0,
                                        marginRight: 4
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        {isActive && isEditingName ? (
                                            <Input
                                                autoFocus
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                onBlur={handleNameSave}
                                                onPressEnter={handleNameSave}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                    height: 24,
                                                    fontSize: 16,
                                                    fontWeight: 600,
                                                    padding: '0 4px',
                                                    background: '#0d1117',
                                                    border: '1px solid #1890ff',
                                                    color: '#fff',
                                                    width: 200
                                                }}
                                            />
                                        ) : (
                                            <Text
                                                style={{
                                                    color: isActive ? '#fff' : '#8b949e',
                                                    fontWeight: isActive ? 600 : 400,
                                                    fontSize: isActive ? 16 : 14,
                                                    lineHeight: '20px',
                                                    cursor: isActive ? 'text' : 'pointer',
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6
                                                }}
                                                onDoubleClick={(e) => {
                                                    if (isActive) {
                                                        e.stopPropagation();
                                                        startEditingName();
                                                    }
                                                }}
                                            >
                                                {isUITab && <AppstoreOutlined style={{ color: isActive ? '#58a6ff' : '#8b949e', fontSize: 12 }} />}
                                                {tabName}
                                                {isActive && (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6 }}>
                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: isSaving ? '#fadb14' : '#52c41a' }} />
                                                        <span style={{ color: '#8b949e', fontSize: 12, fontWeight: 400 }}>{isSaving ? 'Saving...' : 'Saved'}</span>
                                                    </span>
                                                )}
                                            </Text>
                                        )}
                                    </div>

                                    {/* Close button */}
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const newTabs = openTabs.filter(id => id !== tabId);
                                            setOpenTabs(newTabs);
                                            if (isActive) {
                                                if (newTabs.length > 0) {
                                                    navigate(`/canvas/${newTabs[newTabs.length - 1]}`);
                                                } else {
                                                    navigate('/dashboard');
                                                }
                                            }
                                        }}
                                        style={{
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            borderRadius: 4,
                                            background: 'transparent',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        <CloseOutlined style={{ color: '#8b949e', fontSize: 14 }} />
                                    </div>
                                </div>

                                {/* Divider between tabs */}
                                <div style={{ width: 1, height: 32, background: '#30363d', flexShrink: 0 }} />
                            </div>
                        );
                    })}

                    {/* Add canvas dropdown */}
                    <Dropdown
                        trigger={['click']}
                        dropdownRender={() => (
                            <div style={{
                                background: '#161b22',
                                border: '1px solid #30363d',
                                borderRadius: 8,
                                padding: 8,
                                minWidth: 240,
                                maxHeight: 400,
                                overflowY: 'auto',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                            }}>
                                {/* Create New Canvas */}
                                <div
                                    onClick={async () => {
                                        try {
                                            const newCanvas = await createCanvasMutation.mutateAsync({
                                                name: 'Untitled Canvas',
                                                system: { id: 'system', email: 'system@pxlplygrnd.com' }
                                            });
                                            addCanvasToTabs(newCanvas.id);
                                        } catch (error) {
                                            console.error('Failed to create canvas:', error);
                                            message.error('Failed to create canvas');
                                        }
                                    }}
                                    style={{
                                        padding: '10px 12px',
                                        cursor: 'pointer',
                                        borderRadius: 6,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        transition: 'background 0.2s',
                                        borderBottom: '1px solid #30363d',
                                        marginBottom: 8
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    <PlusOutlined style={{ color: '#52c41a', fontSize: 14 }} />
                                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>Create New Canvas</span>
                                </div>

                                {/* Canvas List - only show ones not already open */}
                                <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, padding: '4px 12px', marginBottom: 4 }}>
                                    ADD TO BAR
                                </div>
                                {allCanvases.filter(c => !openTabs.includes(c.id)).length === 0 ? (
                                    <div style={{ padding: '10px 12px', color: '#8b949e', fontSize: 13 }}>
                                        All canvases are open
                                    </div>
                                ) : (
                                    allCanvases.filter(c => !openTabs.includes(c.id)).map((c) => (
                                        <div
                                            key={c.id}
                                            onClick={() => addCanvasToTabs(c.id)}
                                            style={{
                                                padding: '10px 12px',
                                                cursor: 'pointer',
                                                borderRadius: 6,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <AppstoreOutlined style={{ color: '#8b949e', fontSize: 14 }} />
                                            <span style={{
                                                color: '#fff',
                                                fontSize: 14,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1
                                            }}>
                                                {c.name || 'Untitled'}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    >
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                borderRadius: 6,
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid #30363d',
                                transition: 'all 0.2s',
                                flexShrink: 0
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                                e.currentTarget.style.borderColor = '#484f58';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                e.currentTarget.style.borderColor = '#30363d';
                            }}
                        >
                            <PlusOutlined style={{ color: '#fff', fontSize: 14 }} />
                        </div>
                    </Dropdown>
                </div>
                {/* Conditional Header Actions */}
                {isUIDesignCanvas ? (
                    /* UI Design Canvas Toolbar */
                    <Space size={8}>
                        <Button.Group>
                            <Button
                                type={uiViewMode === 'all' ? 'primary' : 'text'}
                                icon={<AppstoreOutlined />}
                                onClick={() => setUiViewMode('all')}
                            />
                            <Button
                                type={uiViewMode === 'mobile' ? 'primary' : 'text'}
                                icon={<MobileOutlined />}
                                onClick={() => setUiViewMode('mobile')}
                            />
                            <Button
                                type={uiViewMode === 'tablet' ? 'primary' : 'text'}
                                icon={<TabletOutlined />}
                                onClick={() => setUiViewMode('tablet')}
                            />
                            <Button
                                type={uiViewMode === 'desktop' ? 'primary' : 'text'}
                                icon={<DesktopOutlined />}
                                onClick={() => setUiViewMode('desktop')}
                            />
                        </Button.Group>

                        <Button.Group>
                            <Button
                                type={uiShowGrid ? 'primary' : 'text'}
                                icon={<BorderOutlined />}
                                onClick={() => setUiShowGrid(!uiShowGrid)}
                            />
                        </Button.Group>

                        <Button.Group>
                            <Button type="text" icon={<UndoOutlined />} disabled />
                            <Button type="text" icon={<RedoOutlined />} disabled />
                        </Button.Group>
                    </Space>
                ) : (
                    /* Media Canvas Toolbar */
                    <Space size={16}>
                        <Popover
                            open={colorPalettesVisible}
                            onOpenChange={setColorPalettesVisible}
                            content={<ColorPalettesPopover canvasId={canvasId} />}
                            trigger="click"
                            placement="bottomRight"
                            overlayStyle={{ padding: 0 }}
                            overlayInnerStyle={{
                                background: '#161b22',
                                border: '1px solid #30363d',
                                borderRadius: 8,
                                padding: 0,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                            }}
                        >
                            <Button
                                type="text"
                                icon={<BgColorsOutlined />}
                                style={{
                                    color: colorPalettesVisible ? '#58a6ff' : '#c9d1d9',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    borderRadius: 10
                                }}
                            >
                                Colors
                            </Button>
                        </Popover>
                        <Button
                            type="text"
                            icon={<FileTextOutlined />}
                            onClick={() => setPromptLibraryVisible(prev => !prev)}
                            style={{ color: promptLibraryVisible ? '#58a6ff' : '#c9d1d9', fontWeight: 500, fontSize: 14, borderRadius: 10 }}
                        >
                            Prompts
                        </Button>
                        <Button
                            type="text"
                            icon={<ScissorOutlined />}
                            onClick={() => {
                                if (scrapbookOpen) {
                                    setScrapbookOpen(false);
                                } else {
                                    // Open scrapbook with selected node's image if available
                                    const selectedNode = nodes.find(n => n.selected && (n.type === 'media' || n.type === 'qwen') && n.data?.src);
                                    setScrapbookImage(selectedNode?.data?.src || null);
                                    setScrapbookOpen(true);
                                }
                            }}
                            style={{ color: scrapbookOpen ? '#58a6ff' : '#c9d1d9', fontWeight: 500, fontSize: 14, borderRadius: 10 }}
                        >
                            Scrapbook
                        </Button>
                        <Button
                            type="text"
                            icon={<FontSizeOutlined />}
                            onClick={() => setGlyphEditorOpen(prev => !prev)}
                            style={{ color: glyphEditorOpen ? '#58a6ff' : '#c9d1d9', fontWeight: 500, fontSize: 14, borderRadius: 10 }}
                        >
                            Glyphs
                        </Button>
                        <Button type="primary" style={{ borderRadius: 10 }} onClick={() => setDrawerVisible(prev => !prev)}>ASSETS</Button>
                    </Space>
                )}
            </Header>

            {/* Conditional Content - UI Design Canvas or Media Canvas */}
            {isUIDesignCanvas ? (
                <UIDesignEditor />
            ) : (
                <Content ref={reactFlowWrapper} style={{ position: 'relative', height: 'calc(100vh - 64px)' }}>
                    <RightPanelStack canvasId={canvasId} nodes={nodes} />
                    {viewMode === 'canvas' ? (
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={handleNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            onNodeDragStart={onNodeDragStart}
                            onNodeDragStop={onNodeDragStop}
                            onSelectionDragStop={onSelectionDragStop}
                            onEdgeClick={onEdgeClick}
                            onDragOver={onDragOver}
                            onDrop={onDrop}
                            nodeTypes={nodeTypes}
                            fitView
                            style={{
                                background: '#0d1117',
                                cursor: activeTool === 'cut' ? 'crosshair' : 'default',
                                pointerEvents: previewVisible ? 'none' : 'auto'
                            }}
                            defaultEdgeOptions={{ style: { cursor: 'default' } }}
                            // Unified pan/zoom like UI canvas: two-finger/scroll zooms, click-drag on canvas pans, click-drag on nodes moves them
                            // Shift+drag creates selection box for multi-select
                            panOnScroll={false}
                            zoomOnScroll={!previewVisible}
                            panOnDrag={!previewVisible}
                            selectionOnDrag={!previewVisible}
                            selectionKeyCode="Shift"
                            nodesDraggable={!previewVisible}
                            nodesConnectable={!previewVisible}
                            elementsSelectable={!previewVisible}
                            zoomOnDoubleClick={false}
                            minZoom={0.01}
                            onlyRenderVisibleElements={true}
                            onMoveStart={onMoveStart}
                            onMoveEnd={onMoveEnd}
                            onNodeDoubleClick={previewVisible ? undefined : onNodeDoubleClick}
                            onNodeClick={previewVisible ? undefined : onNodeClick}
                            onPaneClick={previewVisible ? undefined : onPaneClick}
                        >
                            <Background color="#30363d" gap={20} size={1} />
                            <Controls style={{ display: 'none' }} />

                            {/* MiniMap */}
                            <MiniMap
                                position="bottom-right"
                                style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8, marginBottom: 24, marginRight: 24, width: 150, height: 100 }}
                                nodeColor="#30363d"
                                maskColor="rgba(0, 0, 0, 0.6)"
                                zoomable
                                pannable
                            />

                            {/* Left Sidebar: Pinterest + Midjourney Panel + Media Gallery */}
                            <Panel position="bottom-left" style={{
                                marginLeft: 12,
                                marginBottom: 12,
                                height: (mjCollapsed && pinterestCollapsed && geminiCollapsed) ? 'auto' : 'calc(100vh - 100px)', // Dynamic height
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                                gap: 12,
                                pointerEvents: 'none' // allow clicking through empty spaces if needed, but components need pointer-events: auto
                            }}>

                                {/* Gemini Chat Panel */}
                                <div style={{
                                    pointerEvents: 'auto',
                                    flex: geminiCollapsed ? '0 0 auto' : 1,
                                    minHeight: 0,
                                    width: 360,
                                    transition: 'flex 0.3s ease'
                                }}>
                                    <GeminiChatPanel
                                        canvasId={canvasId}
                                        collapsed={geminiCollapsed}
                                        onToggleCollapse={() => setGeminiCollapsed(!geminiCollapsed)}
                                    />
                                </div>

                                {/* Pinterest Panel - Above Midjourney */}
                                <div style={{
                                    pointerEvents: 'auto',
                                    flex: pinterestCollapsed ? '0 0 auto' : 1,
                                    minHeight: 0,
                                    width: 360,
                                    transition: 'flex 0.3s ease'
                                }}>
                                    <PinterestPanel
                                        canvasId={canvasId}
                                        onMediaClick={handleMediaClick}
                                        collapsed={pinterestCollapsed}
                                        onToggleCollapse={() => setPinterestCollapsed(!pinterestCollapsed)}
                                        onAddToCanvas={handleAddMediaToCanvas}
                                    />
                                </div>

                                {/* Midjourney Panel */}
                                <div style={{
                                    pointerEvents: 'auto',
                                    flex: mjCollapsed ? '0 0 auto' : 1,
                                    minHeight: 0,
                                    width: 360,
                                    transition: 'flex 0.3s ease'
                                }}>
                                    <MidjourneyPanel
                                        canvasId={canvasId}
                                        onMediaClick={handleMediaClick}
                                        collapsed={mjCollapsed}
                                        onToggleCollapse={() => setMjCollapsed(!mjCollapsed)}
                                        onAddToCanvas={handleAddMediaToCanvas}
                                    />
                                </div>

                                <div style={{ pointerEvents: 'auto', flex: 0 }}>
                                    <CanvasMediaGallery canvasId={canvasId} onMediaClick={handleMediaClick} nodes={nodes} />
                                </div>
                            </Panel>

                            {/* Center Floating Dock */}
                            <Panel position="bottom-center" style={{ marginBottom: 12 }}>
                                <div style={{
                                    background: '#161b22',
                                    borderRadius: 32,
                                    padding: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid #30363d',
                                    gap: 12,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                                }}>
                                    {/* Primary FAB Trigger */}
                                    <Popover
                                        content={
                                            <div style={{ width: 280 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #30363d' }}>
                                                    <span style={{ color: '#8b949e', fontSize: 12, fontWeight: 600 }}>ADD NODE</span>
                                                    <span style={{ color: '#484f58', fontSize: 10, border: '1px solid #30363d', padding: '2px 4px', borderRadius: 4, cursor: 'pointer' }} onClick={() => setAddNodeMenuOpen(false)}>Esc to close</span>
                                                </div>
                                                <div style={{ display: 'grid', gap: 8, maxHeight: 350, overflowY: 'auto', paddingRight: 4 }}>
                                                    {[
                                                        { icon: <CloudSyncOutlined />, title: 'Upload', desc: 'Picture or Video', color: '#1890ff', type: 'media', subType: 'upload' },
                                                        { icon: <FontSizeOutlined />, title: 'Text Generator', desc: 'AI Content', color: '#52c41a', type: 'text', subType: 'ai-text' },
                                                        { icon: <PictureOutlined />, title: 'Image Generator', desc: 'AI Image', color: '#722ed1', type: 'media', subType: 'ai-image' },
                                                        { icon: <FireOutlined />, title: 'Midjourney', desc: 'MJ Generation', color: '#ffffff', type: 'midjourney', subType: 'default' },
                                                        { icon: <VideoCameraOutlined />, title: 'Video Generator', desc: 'AI Video', color: '#f5222d', type: 'video', subType: 'ai-video' },
                                                        { icon: <GatewayOutlined />, title: 'Qwen 3D Edit', desc: 'Multi-Angle Gen', color: '#eb2f96', type: 'qwen', subType: 'default' },
                                                        { icon: <FileTextOutlined />, title: 'Plain Text Node', desc: 'Simple Note', color: '#fa8c16', type: 'text', subType: 'note' },
                                                        { icon: <ApiOutlined />, title: 'API Node', desc: 'External Integration', color: '#fadb14', type: 'code', subType: 'api' },
                                                        { icon: <ScissorOutlined />, title: 'Image Slicer', desc: 'Slice Images', color: '#ff4d4f', type: 'imageSlicer', subType: 'default' },
                                                        { icon: <ScissorOutlined />, title: 'Text Slicer', desc: 'Slice Text', color: '#eb2f96', type: 'textSlicer', subType: 'default' },
                                                        { icon: <AppstoreOutlined />, title: 'Moodboard', desc: 'Media Canvas', color: '#ffec3d', type: 'moodboard', subType: 'default' },
                                                        { icon: <SoundOutlined />, title: 'Text to Speech', desc: 'ElevenLabs', color: '#eb2f96', type: 'textToSpeech', subType: 'default' },
                                                        { icon: <ScanOutlined />, title: 'Segmentation', desc: 'Moondream3', color: '#13c2c2', type: 'segmentation', subType: 'default' },
                                                        { icon: <DeploymentUnitOutlined />, title: 'Octalysis', desc: 'Gamification', color: '#faad14', type: 'octalysis', subType: 'default' },
                                                        { icon: <BgColorsOutlined />, title: 'Color Analysis', desc: 'Sample & Viz', color: '#eb2f96', type: 'colorAnalysis', subType: 'default' },
                                                        { icon: <BgColorsOutlined />, title: 'Color Palette Display', desc: 'Show Palette', color: '#fa541c', type: 'colorPaletteDisplay', subType: 'default' },
                                                        { icon: <CodepenOutlined />, title: 'Hunyuan 3D', desc: 'Image to 3D', color: '#b026ff', type: 'hunyuan3d', subType: 'default' },
                                                        { icon: <PictureOutlined />, title: 'Topaz Upscale', desc: 'Upscale & Enhance', color: '#13c2c2', type: 'topaz', subType: 'default' },
                                                        { icon: <FontSizeOutlined />, title: 'Font Extractor', desc: 'Pull Fonts', color: '#eb2f96', type: 'fontExtract', subType: 'default' },
                                                        { icon: <PictureOutlined />, title: 'Hunyuan Panorama', desc: '360 Image Gen', color: '#00f3ff', type: 'hunyuanPanorama', subType: 'default' },
                                                        { icon: <VideoCameraOutlined />, title: 'Veo3 Video', desc: 'Google Veo3', color: '#f5222d', type: 'veo', subType: 'default' },
                                                        { icon: <ScanOutlined />, title: 'Marigold Depth', desc: 'Depth Map', color: '#ff0055', type: 'marigoldDepth', subType: 'default' },
                                                        { icon: <EyeOutlined />, title: 'Moondream Query', desc: 'Vision Query', color: '#722ed1', type: 'moondreamQuery', subType: 'default' },
                                                        { icon: <FontSizeOutlined />, title: 'Dynamic Text Builder', desc: 'Combine Text', color: '#13c2c2', type: 'dynamicText', subType: 'default' },
                                                    ].map((item, idx) => (
                                                        <div key={idx} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 12,
                                                            padding: 10,
                                                            borderRadius: 8,
                                                            cursor: 'pointer',
                                                            transition: 'background 0.2s',
                                                        }}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = '#21262d'}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                            onClick={() => addNode(item.type, item.subType)}
                                                        >
                                                            <div style={{
                                                                width: 36, height: 36, borderRadius: 8,
                                                                background: item.color + '22',
                                                                color: item.color,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: 18
                                                            }}>
                                                                {item.icon}
                                                            </div>
                                                            <div>
                                                                <div style={{ color: '#c9d1d9', fontWeight: 500, fontSize: 14 }}>{item.title}</div>
                                                                <div style={{ color: '#8b949e', fontSize: 12 }}>{item.desc}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        }
                                        title={null}
                                        trigger="click"
                                        open={addNodeMenuOpen}
                                        onOpenChange={setAddNodeMenuOpen}
                                        placement="top"
                                        color="#161b22"
                                        overlayInnerStyle={{ border: '1px solid #30363d' }}
                                    >
                                        <Button
                                            type="primary"
                                            shape="circle"
                                            icon={<PlusOutlined style={{ fontSize: 20 }} />}
                                            size="large"
                                            style={{
                                                width: 50,
                                                height: 50,
                                                border: 'none',
                                                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.4)',
                                                background: '#1890ff'
                                            }}
                                        />
                                    </Popover>
                                </div>
                            </Panel>
                        </ReactFlow>
                    ) : (
                        <VideoEditor />
                    )}

                    {/* Media Sidebar */}
                    <Drawer
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <span style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 600, letterSpacing: '0.5px' }}>MEDIA LIBRARY</span>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <Tooltip title="Grid View">
                                        <div
                                            onClick={() => setMediaViewMode('grid')}
                                            style={{
                                                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                background: mediaViewMode === 'grid' ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
                                                borderRadius: 4, cursor: 'pointer', transition: 'all 0.2s',
                                                color: mediaViewMode === 'grid' ? '#1890ff' : '#8b949e'
                                            }}
                                        >
                                            <AppstoreOutlined style={{ fontSize: 14 }} />
                                        </div>
                                    </Tooltip>
                                    <Tooltip title="List View">
                                        <div
                                            onClick={() => setMediaViewMode('list')}
                                            style={{
                                                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                background: mediaViewMode === 'list' ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
                                                borderRadius: 4, cursor: 'pointer', transition: 'all 0.2s',
                                                color: mediaViewMode === 'list' ? '#1890ff' : '#8b949e'
                                            }}
                                        >
                                            <BarsOutlined style={{ fontSize: 14 }} />
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>
                        }
                        closable={false}
                        placement="right"
                        onClose={() => setDrawerVisible(false)}
                        open={drawerVisible}
                        getContainer={false}
                        mask={true}
                        maskStyle={{ background: 'transparent' }}
                        width={mediaDrawerWidth}
                        style={{ position: 'absolute', background: '#0d1117', borderLeft: '1px solid #30363d' }}
                        headerStyle={{ borderBottom: '1px solid #30363d', background: '#161b22', padding: '16px 24px', height: 60, display: 'flex', alignItems: 'center' }}
                        bodyStyle={{ background: '#0d1117', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                    >
                        {/* Resizer Handle */}
                        <div
                            onMouseDown={handleDrawerMouseDown}
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 6,
                                cursor: 'col-resize',
                                zIndex: 100,
                                background: 'transparent',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'rgba(24, 144, 255, 0.3)'}
                            onMouseLeave={(e) => {
                                if (!isResizingDrawer.current) e.target.style.background = 'transparent';
                            }}
                        />
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {mediaLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                                </div>
                            ) : (
                                <Tabs
                                    defaultActiveKey="1"
                                    centered
                                    tabBarStyle={{
                                        color: '#8b949e',
                                        borderBottom: '1px solid #30363d',
                                        margin: 0,
                                        fontSize: 13,
                                        background: '#0d1117',
                                        zIndex: 10
                                    }}
                                    style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    className="full-height-tabs"
                                    activeKey={drawerActiveTab}
                                    onChange={setDrawerActiveTab}
                                    items={[
                                        {
                                            key: '1',
                                            label: 'Imported',
                                            children: (
                                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                    {/* Search, Filter & Upload - Pinned */}
                                                    <div style={{ padding: '20px 20px 0 20px', flexShrink: 0 }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                                                            <div style={{ display: 'flex', gap: 8 }}>
                                                                <Input
                                                                    prefix={<SearchOutlined style={{ color: '#8b949e' }} />}
                                                                    placeholder="Search..."
                                                                    style={{ background: '#21262d', border: '1px solid #30363d', color: '#fff', borderRadius: 6, flex: 1 }}
                                                                />
                                                                <Button
                                                                    type="primary"
                                                                    icon={<UploadOutlined />}
                                                                    style={{ borderRadius: 6, width: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                />
                                                            </div>
                                                            {/* Filters */}
                                                            <div style={{ display: 'flex', gap: 8 }}>
                                                                <Select
                                                                    placeholder="Type"
                                                                    value={mediaFilterType}
                                                                    onChange={setMediaFilterType}
                                                                    style={{ flex: 1 }}
                                                                    dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                                    options={[
                                                                        { value: 'all', label: 'All Types' },
                                                                        { value: 'image', label: 'Images' },
                                                                        { value: 'video', label: 'Videos' },
                                                                        { value: 'model', label: '3D Models' },
                                                                        { value: 'audio', label: 'Audio' },
                                                                    ]}
                                                                />
                                                                {availableCanvases.length > 0 && (
                                                                    <Select
                                                                        placeholder="Canvas"
                                                                        value={mediaFilterCanvas}
                                                                        onChange={setMediaFilterCanvas}
                                                                        style={{ flex: 1 }}
                                                                        dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                                        options={[
                                                                            { value: 'all', label: 'All Canvases' },
                                                                            ...availableCanvases.map(c => ({ value: c, label: c })),
                                                                            { value: 'Unknown', label: 'Unknown' }
                                                                        ]}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Media Grid - Scrollable */}
                                                    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                                                        {filteredImportedMedia.length === 0 ? (
                                                            <div style={{ color: '#8b949e', textAlign: 'center', gridColumn: '1 / -1' }}>No media found.</div>
                                                        ) : (
                                                            filteredImportedMedia.map((item, index) => (
                                                                <div
                                                                    key={item.id}
                                                                    draggable
                                                                    onDragStart={(e) => {
                                                                        e.dataTransfer.setData('application/json', JSON.stringify(item));
                                                                        e.dataTransfer.effectAllowed = 'move';
                                                                        setTimeout(() => setDrawerVisible(false), 0);
                                                                    }}
                                                                    onClick={() => handleMediaClick(index, filteredImportedMedia)}
                                                                    style={{ position: 'relative', aspectRatio: mediaViewMode === 'grid' ? '1/1' : '16/9', borderRadius: 8, overflow: 'hidden', border: '1px solid #30363d', cursor: 'pointer' }}
                                                                >
                                                                    {item.type === 'audio' ? (
                                                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1c2128', color: '#eb2f96' }}>
                                                                            <SoundOutlined style={{ fontSize: 32 }} />
                                                                        </div>
                                                                    ) : item.type === 'model' ? (
                                                                        <div style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            background: 'linear-gradient(135deg, #2a1261 0%, #161b22 100%)',
                                                                            color: '#b026ff',
                                                                            position: 'relative'
                                                                        }}>
                                                                            <CodepenOutlined style={{ fontSize: 32 }} />
                                                                            <div style={{
                                                                                position: 'absolute',
                                                                                bottom: 6,
                                                                                right: 6,
                                                                                background: 'rgba(0,0,0,0.6)',
                                                                                padding: '2px 4px',
                                                                                borderRadius: 3,
                                                                                fontSize: 10,
                                                                                fontWeight: 'bold',
                                                                                color: '#fff'
                                                                            }}>
                                                                                3D
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <img src={item.src} alt="media" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                    )}
                                                                    {item.type === 'video' && (
                                                                        <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', padding: '2px 4px', borderRadius: 2, fontSize: 10, color: '#fff', fontWeight: 600 }}>VIDEO</div>
                                                                    )}
                                                                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px', background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}>
                                                                        <div style={{ color: '#fff', fontSize: 12, fontWeight: 500, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{item.label}</div>
                                                                        <div style={{ color: '#8b949e', fontSize: 10 }}>Media Asset</div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                        {/* New Placeholder */}
                                                        <div style={{
                                                            aspectRatio: mediaViewMode === 'grid' ? '1/1' : 'auto',
                                                            height: mediaViewMode === 'list' ? 80 : 'auto',
                                                            borderRadius: 8,
                                                            border: '1px dashed #30363d',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            background: '#161b22',
                                                            cursor: 'pointer',
                                                            color: '#8b949e'
                                                        }}>
                                                            <PlusOutlined style={{ fontSize: 24, marginBottom: 4 }} />
                                                            <span style={{ fontSize: 12 }}>NEW</span>
                                                        </div>
                                                        {/* Sentinel for infinite scroll */}
                                                        <div ref={importedSentinelRef} style={{ height: 20 }} />
                                                        {isFetchingMoreUploads && (
                                                            <div style={{ display: 'flex', justifyContent: 'center', padding: 16, gridColumn: '1 / -1' }}>
                                                                <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        },
                                        {
                                            key: '2',
                                            label: 'Generated',
                                            children: (
                                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                    {/* Filters - Pinned */}
                                                    <div style={{ padding: '20px 20px 0 20px', flexShrink: 0 }}>
                                                        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                                                            <Select
                                                                placeholder="Type"
                                                                value={mediaFilterType}
                                                                onChange={setMediaFilterType}
                                                                style={{ flex: 1 }}
                                                                dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                                options={[
                                                                    { value: 'all', label: 'All Types' },
                                                                    { value: 'image', label: 'Images' },
                                                                    { value: 'video', label: 'Videos' },
                                                                    { value: 'model', label: '3D Models' },
                                                                    { value: 'audio', label: 'Audio' },
                                                                ]}
                                                            />
                                                            {availableCanvases.length > 0 && (
                                                                <Select
                                                                    placeholder="Canvas"
                                                                    value={mediaFilterCanvas}
                                                                    onChange={setMediaFilterCanvas}
                                                                    style={{ flex: 1 }}
                                                                    dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                                    options={[
                                                                        { value: 'all', label: 'All Canvases' },
                                                                        ...availableCanvases.map(c => ({ value: c, label: c })),
                                                                        { value: 'Unknown', label: 'Unknown' }
                                                                    ]}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                    {/* Media Grid - Scrollable */}
                                                    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                                                        {generationsLoading ? (
                                                            <div style={{ color: '#8b949e', textAlign: 'center', padding: 40 }}><Spin indicator={<LoadingOutlined spin />} /></div>
                                                        ) : filteredGeneratedMedia.length === 0 ? (
                                                            <div style={{ color: '#8b949e', textAlign: 'center' }}>No generated assets yet.</div>
                                                        ) : (
                                                            <div style={{ display: 'grid', gridTemplateColumns: mediaViewMode === 'grid' ? 'repeat(auto-fill, minmax(130px, 1fr))' : '1fr', gap: 12 }}>
                                                                {filteredGeneratedMedia.map((item, index) => (
                                                                    <div
                                                                        key={item.id}
                                                                        className="generated-media-item"
                                                                        draggable
                                                                        onDragStart={(e) => {
                                                                            e.dataTransfer.setData('application/json', JSON.stringify(item));
                                                                            e.dataTransfer.effectAllowed = 'move';
                                                                            setTimeout(() => setDrawerVisible(false), 0);
                                                                        }}
                                                                        onClick={() => handleMediaClick(index, filteredGeneratedMedia)}
                                                                        style={{ position: 'relative', aspectRatio: mediaViewMode === 'grid' ? '1/1' : '16/9', borderRadius: 8, overflow: 'hidden', border: '1px solid #30363d', cursor: 'pointer', transition: 'border-color 0.2s' }}
                                                                    >
                                                                        {item.type === 'audio' ? (
                                                                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1c2128', color: '#eb2f96' }}>
                                                                                <SoundOutlined style={{ fontSize: 32 }} />
                                                                            </div>
                                                                        ) : item.type === 'model' ? (
                                                                            <div style={{
                                                                                width: '100%',
                                                                                height: '100%',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                background: 'linear-gradient(135deg, #2a1261 0%, #161b22 100%)',
                                                                                color: '#b026ff',
                                                                                position: 'relative'
                                                                            }}>
                                                                                <CodepenOutlined style={{ fontSize: 32 }} />
                                                                                <div style={{
                                                                                    position: 'absolute',
                                                                                    bottom: 6,
                                                                                    right: 6,
                                                                                    background: 'rgba(0,0,0,0.6)',
                                                                                    padding: '2px 4px',
                                                                                    borderRadius: 3,
                                                                                    fontSize: 10,
                                                                                    fontWeight: 'bold',
                                                                                    color: '#fff'
                                                                                }}>
                                                                                    3D
                                                                                </div>
                                                                            </div>
                                                                        ) : item.type === 'video' ? (
                                                                            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                                                <video
                                                                                    src={item.src}
                                                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                    muted
                                                                                    loop
                                                                                    playsInline
                                                                                    preload="none"
                                                                                    onMouseOver={e => e.target.play().catch(() => { })}
                                                                                    onMouseOut={e => { e.target.pause(); e.target.currentTime = 0; }}
                                                                                />
                                                                                <div style={{
                                                                                    position: 'absolute',
                                                                                    top: 6,
                                                                                    left: 6,
                                                                                    background: 'rgba(0,0,0,0.6)',
                                                                                    padding: '2px 6px',
                                                                                    borderRadius: 3,
                                                                                    fontSize: 10,
                                                                                    fontWeight: 'bold',
                                                                                    color: '#fff',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 4
                                                                                }}>
                                                                                    <VideoCameraOutlined style={{ fontSize: 10 }} />
                                                                                    VIDEO
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <img src={item.src} alt="media" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                        )}
                                                                        {/* Delete button */}
                                                                        <Popconfirm
                                                                            title="Delete media?"
                                                                            description="This will permanently delete this media."
                                                                            onConfirm={(e) => handleDeleteGeneratedMedia(e, item.id)}
                                                                            onCancel={(e) => e.stopPropagation()}
                                                                            okText="Delete"
                                                                            cancelText="Cancel"
                                                                            okButtonProps={{ danger: true }}
                                                                        >
                                                                            <div
                                                                                className="drawer-delete-btn"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    top: 6,
                                                                                    right: 6,
                                                                                    width: 24,
                                                                                    height: 24,
                                                                                    borderRadius: 4,
                                                                                    background: 'rgba(0,0,0,0.7)',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    cursor: 'pointer',
                                                                                    opacity: 0,
                                                                                    transition: 'opacity 0.2s, background 0.2s',
                                                                                    zIndex: 10
                                                                                }}
                                                                            >
                                                                                {deletingMediaId === item.id ? (
                                                                                    <LoadingOutlined style={{ color: '#ff4d4f', fontSize: 12 }} spin />
                                                                                ) : (
                                                                                    <DeleteOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
                                                                                )}
                                                                            </div>
                                                                        </Popconfirm>
                                                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px', background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}>
                                                                            <div style={{ color: '#fff', fontSize: 12, fontWeight: 500, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{item.label}</div>
                                                                            <div style={{ color: '#8b949e', fontSize: 10 }}>Generated</div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {/* Sentinel for infinite scroll */}
                                                        <div ref={generatedSentinelRef} style={{ height: 20 }} />
                                                        {isFetchingMoreGenerations && (
                                                            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                                                                <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        },
                                        {
                                            key: '3',
                                            label: 'Inspiration',
                                            children: (
                                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                    {/* Folder Filter - Pinned */}
                                                    <div style={{ padding: '20px 20px 0 20px', flexShrink: 0 }}>
                                                        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                                                            <Select
                                                                placeholder="Folder"
                                                                value={inspirationFolderFilter}
                                                                onChange={setInspirationFolderFilter}
                                                                style={{ flex: 1 }}
                                                                dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                                options={inspirationFolders}
                                                            />
                                                        </div>
                                                    </div>
                                                    {/* Media Grid - Scrollable */}
                                                    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                                                        {filteredInspirationMedia.length === 0 ? (
                                                            <div style={{ color: '#8b949e', textAlign: 'center' }}>No inspiration media yet. Upload from the Dashboard!</div>
                                                        ) : (
                                                            <div style={{ display: 'grid', gridTemplateColumns: mediaViewMode === 'grid' ? 'repeat(auto-fill, minmax(130px, 1fr))' : '1fr', gap: 12 }}>
                                                                {filteredInspirationMedia.map((item, index) => (
                                                                    <div
                                                                        key={item.id}
                                                                        className="inspiration-media-item"
                                                                        draggable
                                                                        onDragStart={(e) => {
                                                                            e.dataTransfer.setData('application/json', JSON.stringify(item));
                                                                            e.dataTransfer.effectAllowed = 'move';
                                                                            setTimeout(() => setDrawerVisible(false), 0);
                                                                        }}
                                                                        onClick={() => handleMediaClick(index, filteredInspirationMedia)}
                                                                        style={{ position: 'relative', aspectRatio: mediaViewMode === 'grid' ? '1/1' : '16/9', borderRadius: 8, overflow: 'hidden', border: '1px solid #30363d', cursor: 'pointer', transition: 'border-color 0.2s' }}
                                                                    >
                                                                        {item.type === 'video' ? (
                                                                            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                                                <video
                                                                                    src={item.src}
                                                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                    muted
                                                                                    loop
                                                                                    playsInline
                                                                                    preload="none"
                                                                                    onMouseOver={e => e.target.play().catch(() => { })}
                                                                                    onMouseOut={e => { e.target.pause(); e.target.currentTime = 0; }}
                                                                                />
                                                                                <div style={{
                                                                                    position: 'absolute',
                                                                                    top: 6,
                                                                                    left: 6,
                                                                                    background: 'rgba(0,0,0,0.6)',
                                                                                    padding: '2px 6px',
                                                                                    borderRadius: 3,
                                                                                    fontSize: 10,
                                                                                    fontWeight: 'bold',
                                                                                    color: '#fff',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 4
                                                                                }}>
                                                                                    <VideoCameraOutlined style={{ fontSize: 10 }} />
                                                                                    VIDEO
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <img src={item.src} alt="media" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                        )}
                                                                        {/* Delete button */}
                                                                        <Popconfirm
                                                                            title="Delete inspiration media?"
                                                                            description="This will permanently delete this file."
                                                                            onConfirm={(e) => handleDeleteInspirationMedia(e, item)}
                                                                            onCancel={(e) => e.stopPropagation()}
                                                                            okText="Delete"
                                                                            cancelText="Cancel"
                                                                            okButtonProps={{ danger: true }}
                                                                        >
                                                                            <div
                                                                                className="drawer-delete-btn"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    top: 6,
                                                                                    right: 6,
                                                                                    width: 24,
                                                                                    height: 24,
                                                                                    borderRadius: 4,
                                                                                    background: 'rgba(0,0,0,0.7)',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    cursor: 'pointer',
                                                                                    opacity: 0,
                                                                                    transition: 'opacity 0.2s, background 0.2s',
                                                                                    zIndex: 10
                                                                                }}
                                                                            >
                                                                                {deletingMediaId === item.id ? (
                                                                                    <LoadingOutlined style={{ color: '#ff4d4f', fontSize: 12 }} spin />
                                                                                ) : (
                                                                                    <DeleteOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
                                                                                )}
                                                                            </div>
                                                                        </Popconfirm>
                                                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px', background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}>
                                                                            <div style={{ color: '#fff', fontSize: 12, fontWeight: 500, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{item.label}</div>
                                                                            <div style={{ color: '#faad14', fontSize: 10, textTransform: 'capitalize' }}>{item.folder}</div>
                                                                            {item.notes && (
                                                                                <div style={{ color: '#8b949e', fontSize: 10, marginTop: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={item.notes}>
                                                                                    {item.notes}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {/* Sentinel for infinite scroll */}
                                                        <div ref={inspirationSentinelRef} style={{ height: 20 }} />
                                                        {isFetchingMoreInspiration && (
                                                            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                                                                <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        },
                                        {
                                            key: '4',
                                            label: 'Sref Styles',
                                            children: (
                                                <SrefStylesTab
                                                    onMediaClick={handleMediaClick}
                                                    mediaViewMode={mediaViewMode}
                                                />
                                            )
                                        }
                                    ]}
                                />
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '16px 20px', borderTop: '1px solid #30363d', background: '#161b22' }}>
                            <div style={{ fontSize: 11, color: '#8b949e', textAlign: 'center' }}>
                                <span>{importedMedia.length + generatedMedia.length + inspirationMedia.length} media items</span>
                            </div>
                            <style>{`
                                .generated-media-item:hover,
                                .inspiration-media-item:hover {
                                    border-color: #1890ff !important;
                                }
                                .generated-media-item:hover .drawer-delete-btn,
                                .inspiration-media-item:hover .drawer-delete-btn {
                                    opacity: 1 !important;
                                }
                                .drawer-delete-btn:hover {
                                    background: rgba(255, 77, 79, 0.3) !important;
                                }
                            `}</style>
                        </div>
                    </Drawer>
                    <PromptLibraryDrawer
                        visible={promptLibraryVisible}
                        onClose={() => setPromptLibraryVisible(false)}
                        onCopyToCanvas={handleCopyPromptToCanvas}
                    />
                    <ScrapbookPanel
                        open={scrapbookOpen}
                        onClose={() => setScrapbookOpen(false)}
                        initialImage={scrapbookImage}
                        canvasId={canvasId}
                        onAddToCanvas={handleAddMediaToCanvas}
                    />
                    <GlyphEditorPanel
                        open={glyphEditorOpen}
                        onClose={() => setGlyphEditorOpen(false)}
                        canvasId={canvasId}
                    />
                </Content>
            )
            }

            <MediaPreviewModal
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                initialIndex={previewStartIndex}
                mediaItems={previewMediaList}
            />

            {/* Slot Selection Modal for multi-input nodes */}
            <Modal
                title="Select Input Slot"
                open={slotSelectionVisible}
                onCancel={() => {
                    setSlotSelectionVisible(false);
                    setPendingConnection(null);
                    setAvailableSlots([]);
                }}
                footer={null}
                centered
                width={400}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <p style={{ color: '#8b949e', marginBottom: 8 }}>
                        Choose where to connect this image:
                    </p>
                    {availableSlots.map((slot) => (
                        <Button
                            key={slot.id}
                            type="default"
                            size="large"
                            onClick={() => handleSlotSelect(slot.id)}
                            style={{
                                height: 'auto',
                                padding: '12px 16px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                textAlign: 'left',
                                border: slot.required ? '2px solid #722ed1' : '1px solid #30363d',
                                background: slot.required ? 'rgba(114, 46, 209, 0.1)' : '#161b22'
                            }}
                        >
                            <span style={{ fontWeight: 'bold', fontSize: 14 }}>
                                {slot.label} {slot.required && <span style={{ color: '#ff4d4f' }}>★</span>}
                            </span>
                            <span style={{ fontSize: 12, color: '#8b949e', marginTop: 4 }}>
                                {slot.description}
                            </span>
                        </Button>
                    ))}
                </div>
            </Modal>
        </Layout >
    );
};

const EditorWithProvider = () => (
    <ReactFlowProvider>
        <UIDesignProvider>
            <Editor />
        </UIDesignProvider>
    </ReactFlowProvider>
);

export default EditorWithProvider;
